import { useId, useState } from 'react'
import { useMe } from '@/auth/useMe'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TASK_PRIORITY_LABELS } from '@/features/preinvest/types'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { todayIso } from '../dates'
import { addDays } from '../record/format'
import { createRelationTask } from './api'
import { clearDraft, readDraft, taskDraftKey, writeDraft } from './drafts'
import type { PlannedTask, RelationRef, TaskPriority } from './types'

interface TaskDraft {
  title: string
  due_date: string
  /** The chosen owner; empty until someone is picked, then it is the person who is logged in. */
  owner: string
  priority: TaskPriority
}

const PRIORITIES: TaskPriority[] = ['low', 'normal', 'high']

const PICKS = [
  { label: 'Morgen', days: 1 },
  { label: 'Volgende week', days: 7 },
  { label: 'Over een maand', days: 30 },
] as const

const selectClass =
  'h-8 w-full rounded-md border border-border bg-background px-2 text-sm max-md:h-11 max-md:text-base'

function freshDraft(): TaskDraft {
  return { title: '', due_date: addDays(todayIso(), 7), owner: '', priority: 'normal' }
}

function isTaskDraft(value: unknown): value is TaskDraft {
  if (typeof value !== 'object' || value === null) return false
  const draft = value as Record<string, unknown>
  return (
    typeof draft.title === 'string' &&
    typeof draft.due_date === 'string' &&
    typeof draft.owner === 'string' &&
    PRIORITIES.some((priority) => priority === draft.priority)
  )
}

/**
 * The task dialog of a relation page (the quick action Taak, ED-03): a title (empty becomes
 * "Opvolgen: <naam>"), a due date with quick picks, an owner (default: me) and a priority.
 * On a contact page the task hangs on the contact, on an organisation page on the
 * organisation. The draft lives in `sessionStorage`: Escape or × keep it, "Annuleren" and a
 * successful save clear it (IA-15).
 */
export function TaskDialog({
  open,
  onOpenChange,
  relation,
  onCreated,
  onMakeAgreement,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  relation: RelationRef & { name: string }
  onCreated: (task: PlannedTask) => void
  /** Shows "Moet dit vaker? Maak er een afspraak van" (design §4.5); the caller opens the agreement dialog. */
  onMakeAgreement?: () => void
}) {
  const { me } = useMe()
  const { membersById } = useTeamMembers()
  const key = taskDraftKey(relation)
  const [draft, setDraft] = useState<TaskDraft>(() => readDraft(key, isTaskDraft) ?? freshDraft())
  const [draftKey, setDraftKey] = useState(key)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const titleId = useId()
  const dateId = useId()
  const ownerId = useId()
  const priorityId = useId()

  // Another relation has its own draft ("adjusting state when a prop changes").
  if (draftKey !== key) {
    setDraftKey(key)
    setDraft(readDraft(key, isTaskDraft) ?? freshDraft())
    setError(null)
  }

  // The team, plus me when I am not in it (an admin may not be).
  const team = membersById
    ? [...membersById.values()]
        .map((member) => ({ id: member.id, name: member.display_name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'nl'))
    : []
  const owners = me && !team.some((member) => member.id === me.id) ? [{ id: me.id, name: me.full_name }, ...team] : team
  const owner = owners.some((member) => member.id === draft.owner) ? draft.owner : (me?.id ?? '')

  function update(patch: Partial<TaskDraft>) {
    const next = { ...draft, ...patch }
    setDraft(next)
    writeDraft(key, next)
  }

  function cancel() {
    clearDraft(key)
    setDraft(freshDraft())
    setError(null)
    onOpenChange(false)
  }

  async function submit() {
    if (busy || !owner) return
    setBusy(true)
    setError(null)
    try {
      const task = await createRelationTask({
        title: draft.title.trim() || `Opvolgen: ${relation.name}`,
        due_date: draft.due_date || null,
        priority: draft.priority,
        assignee_ids: [owner],
        ...(relation.kind === 'contact' ? { person_id: relation.id } : { organisation_id: relation.id }),
      })
      clearDraft(key)
      setDraft(freshDraft())
      toast.success('Taak aangemaakt.')
      onOpenChange(false)
      onCreated(task)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Taak aanmaken mislukt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Taak voor {relation.name}</DialogTitle>
          <DialogDescription>De taak staat in Gepland en in Mijn taken van de eigenaar.</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={titleId}>Titel</Label>
            <Input
              id={titleId}
              value={draft.title}
              maxLength={300}
              placeholder={`Opvolgen: ${relation.name}`}
              onChange={(event) => update({ title: event.target.value })}
              className="max-md:h-11"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={dateId}>Vervaldatum</Label>
            <div className="flex flex-wrap items-center gap-2">
              {PICKS.map((pick) => {
                const date = addDays(todayIso(), pick.days)
                const chosen = draft.due_date === date
                return (
                  <Button
                    key={pick.label}
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-pressed={chosen}
                    className={cn(chosen && 'border-primary bg-primary/10')}
                    onClick={() => update({ due_date: date })}
                  >
                    {pick.label}
                  </Button>
                )
              })}
              <Input
                id={dateId}
                type="date"
                value={draft.due_date}
                onChange={(event) => update({ due_date: event.target.value })}
                className="w-40 max-md:h-11"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ownerId}>Eigenaar</Label>
              <select
                id={ownerId}
                value={owner}
                onChange={(event) => update({ owner: event.target.value })}
                className={selectClass}
              >
                {owners.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={priorityId}>Prioriteit</Label>
              <select
                id={priorityId}
                value={draft.priority}
                onChange={(event) => update({ priority: event.target.value as TaskPriority })}
                className={selectClass}
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {TASK_PRIORITY_LABELS[priority]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {onMakeAgreement ? (
            <Button
              type="button"
              variant="link"
              className="self-start px-0"
              onClick={() => {
                onOpenChange(false)
                onMakeAgreement()
              }}
            >
              Moet dit vaker? Maak er een afspraak van
            </Button>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={cancel}>
              Annuleren
            </Button>
            <Button type="submit" disabled={busy || !owner}>
              {busy ? 'Bezig…' : 'Taak aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
